import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import ServicePicker from "@/components/ServicePicker";
import { Slider } from "@/components/ui/slider";
import { PhotoField, CertificationsCard } from "@/components/collector/ProfileMedia";
import { normalizeServiceCode } from "@/lib/services";

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];
const TYPES = [{ v: "mobile", l: "Mobile" }, { v: "stationary", l: "Brick & Mortar" }, { v: "both", l: "Both" }];
const TEXT_FIELDS = [
  { k: "business_name", l: "Business name", span: true, tid: "profile-name-input" },
  { k: "ein", l: "EIN number", ph: "XX-XXXXXXX", tid: "profile-ein-input" },
  { k: "phone", l: "Phone", tid: "profile-phone-input" },
  { k: "email", l: "Email", type: "email", tid: "profile-email-input" },
  { k: "first_name", l: "First name", tid: "profile-first-name-input" },
  { k: "last_name", l: "Last name", tid: "profile-last-name-input" },
  { k: "address", l: "Street Address", tid: "profile-address-input" },
  { k: "city", l: "City", tid: "profile-city-input" },
];

const COMPANY_ONLY = ["business_name", "ein"];

const buildPayload = (p) => {
  const out = basePayload(p);
  if (p.company_id) COMPANY_ONLY.forEach((k) => delete out[k]);
  return out;
};

const basePayload = (p) => ({
  business_name: p.business_name, ein: p.ein, first_name: p.first_name, last_name: p.last_name, bio: p.bio,
  phone: p.phone, email: p.email, service_type: p.service_type, services: p.services,
  service_supplies: p.service_supplies || {}, supplies: p.supplies, service_areas: p.service_areas,
  address: p.address, city: p.city, state: p.state, zip_code: p.zip_code,
  hourly_rate: parseFloat(p.hourly_rate) || 0, photo_url: p.photo_url,
  service_radius_miles: parseInt(p.service_radius_miles, 10) || 100,
});

const RadiusField = ({ profile, set }) => (
  <div className="sm:col-span-2">
    <label className="label-soft">Service radius (miles)</label>
    <div className="max-w-xs">
      <div className="flex items-center gap-3">
        <input data-testid="profile-radius-input" type="number" min="5" max="500" className="input-soft w-24" value={profile.service_radius_miles ?? 100} onChange={(e) => set("service_radius_miles", e.target.value)} placeholder="100"/>
        <span className="text-sm text-[#5C6B64]">miles</span>
      </div>
      <div className="mt-4 pr-1">
        <Slider data-testid="profile-radius-slider" min={5} max={500} step={5} value={[Math.max(5, Math.min(500, Number(profile.service_radius_miles) || 100))]} onValueChange={(v) => set("service_radius_miles", v[0])} />
        <div className="flex justify-between text-[10px] text-[#8A9A86] mt-1.5"><span>5 mi</span><span>500 mi</span></div>
      </div>
      <p className="text-xs text-[#8A9A86] mt-2">Drag the slider or type a value. Matched to customer requests within this many miles of the zip codes. Default is 100.</p>
    </div>
  </div>
);

// Shared collector profile editor. `base` is "/collectors" (own profile) or "/company/collectors/:id".
export const CollectorProfileForm = ({ profile, setProfile, base, reload, title = "Business profile" }) => {
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setProfile({ ...profile, [k]: v });
  // Company collectors can save partial profiles (completion checklist gates leads instead).
  const hasCert = (profile.certifications || []).length > 0 || !!profile.company_id;
  const fields = profile.company_id ? TEXT_FIELDS.filter((f) => !COMPANY_ONLY.includes(f.k)) : TEXT_FIELDS;
  const services = [...new Set((profile.services || []).map(normalizeServiceCode))];

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put(`${base}/profile`, buildPayload(profile));
      setProfile((p) => ({ ...p, ...data }));
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="card-soft space-y-5" data-testid="profile-form">
      <h2 className="font-heading text-xl text-[#1E3F33]">{title}</h2>
      <PhotoField profile={profile} setProfile={setProfile} base={base} />
      {profile.company_id && (
        <p className="text-xs text-[#5C6B64] rounded-lg bg-[#F0EFEA] px-3 py-2" data-testid="profile-company-note">
          Business name and EIN come from <span className="font-medium text-[#1E3F33]">{profile.company_name || "the company"}</span>. Fill in this collector's own contact details, services, service area and certifications below.
        </p>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        {fields.map((f) => (
          <div key={f.k} className={f.span ? "sm:col-span-2" : ""}><label className="label-soft">{f.l}</label>
            <input data-testid={f.tid} type={f.type || "text"} className="input-soft" placeholder={f.ph} value={profile[f.k] || ""} onChange={(e) => set(f.k, e.target.value)}/></div>
        ))}
        <div><label className="label-soft">State</label>
          <select data-testid="profile-state-input" className="input-soft" value={profile.state || ""} onChange={(e) => set("state", e.target.value)}>
            <option value="">Select…</option>
            {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select></div>
        <div><label className="label-soft">Zip code</label>
          <input data-testid="profile-zip-input" className="input-soft" value={profile.zip_code || ""} onChange={(e) => set("zip_code", e.target.value)}/></div>
        <div className="sm:col-span-2">
          <label className="label-soft">Service type</label>
          <div className="flex gap-3">
            {TYPES.map(({ v, l }) => (
              <button type="button" key={v} data-testid={`profile-type-${v}`} onClick={() => set("service_type", v)}
                className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${profile.service_type === v ? "bg-[#1E3F33] text-white border-[#1E3F33]" : "bg-white text-[#1E3F33] border-[#1E3F33]/15 hover:border-[#1E3F33]/40"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="label-soft">Services offered</label>
          <p className="text-xs text-[#8A9A86] mb-3">Tap a category to expand and select every service provided. For each one, mark whether the supplies are stocked.</p>
          <ServicePicker selected={services} supplies={profile.service_supplies || {}} onChange={(svcs, supps) => setProfile({ ...profile, services: svcs, service_supplies: supps })} />
        </div>
        <div className="sm:col-span-2">
          <label className="label-soft">Service area zip codes (comma-separated)</label>
          <input data-testid="profile-areas-input" className="input-soft" value={(profile.service_areas || []).join(", ")}
            onChange={(e) => set("service_areas", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="e.g., 78701, 78702, 78703"/>
          <p className="text-xs text-[#8A9A86] mt-1.5">Customers search by zip code — list every zip served so the profile appears in their results.</p>
        </div>
        <RadiusField profile={profile} set={set} />
      </div>
      <CertificationsCard profile={profile} base={base} reload={reload} />
      <button data-testid="profile-save-button" disabled={saving || !hasCert} title={!hasCert ? "Upload at least one certification first" : ""} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
        {saving ? "Saving…" : (!hasCert ? "Upload a certification to save" : "Save profile")}
      </button>
    </form>
  );
};
